import { useCallback, useState } from 'react'

export const READER_SETTINGS_KEY = 'reader-settings'

export type ReaderSize = 1 | 2 | 3 | 4 | 5
export type ReaderLeading = 'snug' | 'normal' | 'loose'

export interface ReaderSettingsValue {
  size: ReaderSize
  leading: ReaderLeading
}

export const READER_SIZE_REM: Record<ReaderSize, number> = { 1: 1.0, 2: 1.125, 3: 1.25, 4: 1.4, 5: 1.6 }
export const READER_LEADING: Record<ReaderLeading, number> = { snug: 1.55, normal: 1.75, loose: 1.95 }

export const READER_DEFAULTS: ReaderSettingsValue = { size: 3, leading: 'normal' }

const SIZES: readonly ReaderSize[] = [1, 2, 3, 4, 5]
const LEADINGS: readonly ReaderLeading[] = ['snug', 'normal', 'loose']

function isSize(v: unknown): v is ReaderSize {
  return typeof v === 'number' && (SIZES as readonly number[]).includes(v)
}

function isLeading(v: unknown): v is ReaderLeading {
  return typeof v === 'string' && (LEADINGS as readonly string[]).includes(v)
}

/** Validates each field independently; anything unrecognised falls back to its default. */
export function parseReaderSettings(raw: string | null): ReaderSettingsValue {
  if (!raw) return READER_DEFAULTS
  try {
    const parsed: unknown = JSON.parse(raw)
    const obj = typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {}
    return {
      size: isSize(obj.size) ? obj.size : READER_DEFAULTS.size,
      leading: isLeading(obj.leading) ? obj.leading : READER_DEFAULTS.leading,
    }
  } catch {
    return READER_DEFAULTS
  }
}

function read(): ReaderSettingsValue {
  if (typeof window === 'undefined') return READER_DEFAULTS
  try {
    return parseReaderSettings(localStorage.getItem(READER_SETTINGS_KEY))
  } catch {
    return READER_DEFAULTS
  }
}

function write(value: ReaderSettingsValue) {
  try {
    localStorage.setItem(READER_SETTINGS_KEY, JSON.stringify(value))
  } catch {
    // Private mode or storage denied: keep the in-memory value only.
  }
}

function clampSize(n: number): ReaderSize {
  return Math.min(5, Math.max(1, Math.round(n))) as ReaderSize
}

/**
 * Per-device reading passage preferences (size step 1–5, leading snug/normal/loose).
 * Persisted under `reader-settings`; written only on change, never on mount.
 */
export function useReaderSettings() {
  const [settings, setSettings] = useState<ReaderSettingsValue>(read)

  const update = useCallback((patch: Partial<ReaderSettingsValue>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch }
      write(next)
      return next
    })
  }, [])

  const setSize = useCallback((size: number) => update({ size: clampSize(size) }), [update])
  const setLeading = useCallback((leading: ReaderLeading) => update({ leading }), [update])

  return { settings, setSize, setLeading }
}
